import type { IconName } from './icons';

export interface NavItem {
  to: string;
  label: string;
  permission?: string;
  icon: IconName;
  /** Words somebody might type looking for this, beyond its label. */
  finds?: string[];
}

export interface NavSection {
  heading: string;
  /** A group of one: the heading is itself a link, with no dropdown. */
  to?: string;
  icon?: IconName;
  items: NavItem[];
}

/**
 * The whole application, in the order a shop thinks about it.
 *
 * Counter first, because that is where the day happens and where the least
 * experienced person works. Everything an owner or accountant needs sits below,
 * grouped so a section can be shut and forgotten by somebody who never uses it.
 */
/**
 * The whole application, in the order a shop reaches for it.
 *
 * "Every day" is what gets touched between opening and closing: selling,
 * billing, who owes what, what is on the shelf, today's rate, the till. Seven
 * things. Everything below that is real work a shop does, but weekly or
 * monthly or at audit — and putting it at the same weight as Sell is what made
 * this feel like a system to be studied rather than used.
 *
 * Nothing is hidden. A long list you can read beats a short one that makes you
 * remember where something was filed.
 */
export const SECTIONS: NavSection[] = [
  // A direct link, not a group: the one screen everybody opens first.
  {
    heading: 'Home',
    to: '/',
    icon: 'dashboard',
    items: [{ to: '/', label: 'Today', permission: 'dashboard.view', icon: 'dashboard', finds: ['dashboard', 'home'] }],
  },

  {
    heading: 'Sell',
    items: [
      {
        to: '/pos',
        label: 'Sell at the counter',
        permission: 'sales.create',
        icon: 'sell',
        finds: ['till', 'pos', 'bill'],
      },
      { to: '/sales', label: 'Invoices', permission: 'sales.view', icon: 'invoice', finds: ['bills', 'sales'] },
      {
        to: '/estimates',
        label: 'Estimates',
        permission: 'estimate.view',
        icon: 'invoice',
        finds: ['quote', 'quotation'],
      },
      { to: '/customers', label: 'Customers', permission: 'customer.view', icon: 'customer' },
    ],
  },

  {
    heading: 'Savings',
    items: [
      {
        to: '/chits',
        label: 'Savings accounts',
        permission: 'chit.view',
        icon: 'income',
        finds: ['chit', 'scheme', 'passbook', 'instalment', 'savings', 'redeem'],
      },
      {
        to: '/chit-schemes',
        label: 'Schemes',
        permission: 'chit.view',
        icon: 'product',
        finds: ['chit scheme', 'savings plan', '11+1', 'bonus'],
      },
    ],
  },

  {
    heading: 'Buying',
    items: [
      {
        to: '/purchases',
        label: 'Purchases',
        permission: 'purchase.view',
        icon: 'purchase',
        finds: ['buy', 'invoice in'],
      },
      { to: '/old-gold', label: 'Old gold', permission: 'oldgold.post', icon: 'oldGold', finds: ['scrap', 'buy back'] },
      {
        to: '/exchange',
        label: 'Exchange',
        permission: 'exchange.create',
        icon: 'exchange',
        finds: ['old gold for new'],
      },
      {
        to: '/purchase-returns',
        label: 'Returns to supplier',
        permission: 'purchase_return.view',
        icon: 'purchase',
        finds: ['debit note'],
      },
      { to: '/suppliers', label: 'Suppliers', permission: 'supplier.view', icon: 'supplier', finds: ['vendors'] },
    ],
  },

  {
    heading: 'Stock',
    items: [
      {
        to: '/items',
        label: 'Pieces in stock',
        permission: 'item.view',
        icon: 'item',
        finds: ['pieces', 'tags', 'barcode', 'items'],
      },
      {
        to: '/products',
        label: 'Products',
        permission: 'product.view',
        icon: 'product',
        finds: ['designs', 'catalogue'],
      },
      {
        to: '/locations',
        label: 'Locations',
        permission: 'item.view',
        icon: 'audit',
        finds: ['showcase', 'safe', 'strong room', 'tray'],
      },
      { to: '/transfers', label: 'Branch transfers', permission: 'stock.view', icon: 'transfer' },
      {
        to: '/adjustments',
        label: 'Adjustments',
        permission: 'stock.view',
        icon: 'adjust',
        finds: ['write off', 'loss'],
      },
      {
        to: '/audit',
        label: 'Physical count',
        permission: 'stock.audit_conduct',
        icon: 'audit',
        finds: ['stock take'],
      },
    ],
  },

  // Everything that turns metal into other metal: old gold and scrap waiting,
  // melts out at the karigar or refinery, bars back, and work at the goldsmith.
  {
    heading: 'Melting',
    items: [
      {
        to: '/melting?tab=IN_PROCESS',
        label: 'Out for melting',
        permission: 'stock.view',
        icon: 'melting',
        finds: ['melting', 'send for melting', 'refinery', 'refining', 'karigar melt'],
      },
      {
        to: '/melting?tab=PENDING_APPROVAL',
        label: 'Awaiting loss approval',
        permission: 'stock.view',
        icon: 'approval',
        finds: ['melting loss', 'approve melt'],
      },
      {
        to: '/melting?tab=POSTED',
        label: 'Completed melts',
        permission: 'stock.view',
        icon: 'audit',
        finds: ['melting history', 'melt register'],
      },
      {
        to: '/melting?tab=BARS',
        label: 'Bars on hand',
        permission: 'stock.view',
        icon: 'item',
        finds: ['bars', 'bullion', 'fine gold', 'sell bar'],
      },
      {
        to: '/melting/melters',
        label: 'Melters and refiners',
        permission: 'stock.view',
        icon: 'supplier',
        finds: ['refiner', 'karigar', 'melter', 'refiner account', 'loss by melter', 'melter performance'],
      },
      {
        to: '/melting/lots',
        label: 'Old metal and scrap',
        permission: 'stock.view',
        icon: 'oldGold',
        finds: ['old gold lots', 'old silver', 'scrap', 'dust', 'lots'],
      },
      {
        to: '/job-work',
        label: 'Job work',
        permission: 'jobwork.issue',
        icon: 'transfer',
        finds: ['karigar', 'goldsmith', 'making'],
      },
      {
        to: '/melting/position',
        label: 'Where is the metal',
        permission: 'stock.view',
        icon: 'rate',
        finds: ['metal position', 'whereabouts', 'fine weight', 'stock accounts'],
      },
      {
        to: '/reports?group=Melting',
        label: 'Melting reports',
        permission: 'report.view',
        icon: 'report',
        finds: ['melting register', 'bar register', 'loss by melter'],
      },
    ],
  },

  {
    heading: 'Money',
    items: [
      {
        to: '/accounts',
        label: 'Accounts overview',
        permission: 'accounts.view',
        icon: 'ledger',
        finds: ['cash position', 'who owes', 'balances'],
      },
      { to: '/cash', label: 'Cash and bank', permission: 'cash.view', icon: 'cash', finds: ['till count', 'closing'] },
      {
        to: '/payments',
        label: 'Payments',
        permission: 'payment.create',
        icon: 'payment',
        finds: ['pay supplier', 'receipt'],
      },
      {
        to: '/advances',
        label: 'Advances',
        permission: 'payment.create',
        icon: 'payment',
        finds: ['advance', 'booking', 'deposit', 'order advance', 'supplier advance'],
      },
      { to: '/expenses', label: 'Expenses', permission: 'expense.view', icon: 'expense', finds: ['rent', 'salary'] },
      { to: '/income', label: 'Other income', permission: 'income.view', icon: 'income' },
      { to: '/ledgers', label: 'Ledgers', permission: 'accounts.view', icon: 'ledger', finds: ['accounts', 'chart'] },
      {
        to: '/journals',
        label: 'Day book',
        permission: 'accounts.view',
        icon: 'ledger',
        finds: ['journal', 'entries'],
      },
    ],
  },

  // A direct link: the day's rate is checked more often than anything else.
  {
    heading: 'Rates',
    to: '/rates',
    icon: 'rate',
    items: [
      {
        to: '/rates',
        label: 'Metal rates',
        permission: 'rate.view',
        icon: 'rate',
        finds: ['gold rate', 'price', 'rate board'],
      },
    ],
  },

  {
    heading: 'Reports',
    items: [
      // One entry per group, so a person looking for a sales register does not
      // have to read past the stock and control reports to find it.
      {
        to: '/reports?group=Sales',
        label: 'Sales reports',
        permission: 'report.view',
        icon: 'sell',
        finds: ['sales register', 'profit', 'day-wise'],
      },
      {
        to: '/reports?group=Purchase',
        label: 'Purchase reports',
        permission: 'report.view',
        icon: 'purchase',
        finds: ['purchase register', 'old gold'],
      },
      {
        to: '/reports?group=Stock',
        label: 'Stock reports',
        permission: 'report.view',
        icon: 'item',
        finds: ['stock summary', 'dead stock', 'fine weight'],
      },
      {
        to: '/reports?group=Melting',
        label: 'Melting reports',
        permission: 'report.view',
        icon: 'melting',
        finds: ['melting register', 'bars', 'melting loss', 'refinery'],
      },
      {
        to: '/reports?group=Accounting',
        label: 'Accounting and GST',
        permission: 'report.view_financial',
        icon: 'ledger',
        finds: ['day book', 'cash book', 'gst', 'outstanding', 'expenses'],
      },
      {
        to: '/reports?group=Control',
        label: 'Control reports',
        permission: 'report.view',
        icon: 'approval',
        finds: ['adjustments', 'exceptions'],
      },
      {
        to: '/reports',
        label: 'All reports',
        permission: 'report.view',
        icon: 'report',
        finds: ['registers', 'every report'],
      },
      {
        to: '/statements',
        label: 'Financial statements',
        permission: 'report.view_financial',
        icon: 'ledger',
        finds: ['p&l', 'profit and loss', 'balance sheet', 'cash flow', 'trial balance'],
      },
    ],
  },

  {
    heading: 'Setup',
    items: [
      { to: '/approvals', label: 'Approvals', permission: 'approval.decide', icon: 'approval', finds: ['waiting'] },
      {
        to: '/periods',
        label: 'Periods and year end',
        permission: 'accounts.view',
        icon: 'period',
        finds: ['close month'],
      },
      {
        to: '/users',
        label: 'Users and roles',
        permission: 'user.view',
        icon: 'user',
        finds: ['staff', 'permissions'],
      },
      {
        to: '/controls',
        label: 'Controls and approvals',
        permission: 'settings.view',
        icon: 'approval',
        finds: ['limit', 'approval', 'discount limit', 'cash limit'],
      },
      {
        to: '/lists',
        label: 'Lists and categories',
        permission: 'master.view',
        icon: 'product',
        finds: ['expense head', 'category', 'brand', 'collection', 'unit', 'department', 'design', 'stone', 'diamond'],
      },
      {
        to: '/charges',
        label: 'Charges and taxes',
        permission: 'master.view',
        icon: 'rate',
        finds: ['gst', 'tax', 'making charge', 'wastage', 'va', 'pricing rule', 'slab'],
      },
      {
        to: '/metals',
        label: 'Metals and purities',
        permission: 'master.view',
        icon: 'rate',
        finds: ['purity', '916', '22k', 'karat', 'fineness'],
      },
      {
        to: '/settings',
        label: 'Settings',
        permission: 'settings.view',
        icon: 'product',
        finds: ['shop details', 'gst', 'invoice'],
      },
    ],
  },
];
